import { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { API, BACKEND_URL, useAuth } from "../../App";
import { 
  MessageCircle, Check, X, Star, Eye, EyeOff,
  Play, User, Calendar, Filter, Monitor, Hand, PlayCircle, Save, Settings,
  Trash2, Ticket
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogTitle, DialogHeader } from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

export default function CoupleTreeManager() {
  const { wedding, setWedding } = useOutletContext();
  const { user } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [showClearDialog, setShowClearDialog] = useState(false);
  const [clearConfirmText, setClearConfirmText] = useState("");
  const [clearing, setClearing] = useState(false);
  const [treeSettings, setTreeSettings] = useState(wedding?.tree_settings || {
    default_mode: "interactive",
    autoplay_delay: 8,
    autoplay_videos_enabled: true,
    display_delay: 5,
    idle_timeout: 30,
  });

  useEffect(() => {
    fetchSubmissions();
  }, [wedding]);

  const fetchSubmissions = async () => {
    if (!wedding?.wedding_id) return;
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API}/tree/${wedding.wedding_id}/all`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSubmissions(response.data);
    } catch (error) {
      console.error("Failed to fetch submissions:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateSubmissionStatus = async (submissionId, status) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API}/tree/${submissionId}`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSubmissions(submissions.map(s => 
        s.submission_id === submissionId ? { ...s, status } : s
      ));
      toast.success(`Submission ${status}`);
    } catch (error) {
      toast.error("Failed to update submission");
    }
  };

  const deleteSubmission = async (submissionId) => {
    if (!window.confirm("Are you sure you want to delete this submission?")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(
        `${API}/tree/${submissionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSubmissions(submissions.filter(s => s.submission_id !== submissionId));
      toast.success("Submission deleted");
    } catch (error) {
      toast.error("Failed to delete submission");
    }
  };

  const filteredSubmissions = submissions.filter(s => {
    if (filter === "all") return s.status !== "archived";
    return s.status === filter;
  });

  const toggleRaffleEligible = async (submissionId, currentVal) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API}/tree/${submissionId}`,
        { raffle_eligible: !currentVal },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSubmissions(submissions.map(s =>
        s.submission_id === submissionId ? { ...s, raffle_eligible: !currentVal } : s
      ));
      toast.success(!currentVal ? "Raffle eligible" : "Excluded from raffle");
    } catch {
      toast.error("Failed to update");
    }
  };

  const clearAllEntries = async () => {
    if (clearConfirmText !== "CLEAR ALL") return;
    setClearing(true);
    try {
      const token = localStorage.getItem("token");
      const r = await axios.post(
        `${API}/tree/${wedding.wedding_id}/clear`,
        { confirmation: "CLEAR ALL" },
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
      );
      toast.success(`Archived ${r.data.archived_count} entries`);
      setShowClearDialog(false);
      setClearConfirmText("");
      fetchSubmissions();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to clear entries");
    } finally {
      setClearing(false);
    }
  };

  const saveTreeSettings = async () => {
    setSavingSettings(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(
        `${API}/weddings/${wedding.wedding_id}`,
        { tree_settings: treeSettings },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (setWedding) setWedding(response.data);
      toast.success("Tree settings saved!");
    } catch {
      toast.error("Failed to save settings");
    } finally {
      setSavingSettings(false);
    }
  };

  const statusCounts = {
    all: submissions.length,
    pending: submissions.filter(s => s.status === "pending").length,
    approved: submissions.filter(s => s.status === "approved").length,
    featured: submissions.filter(s => s.status === "featured").length,
    hidden: submissions.filter(s => s.status === "hidden").length,
  };

  if (!wedding) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-wedding-ivory/60">No wedding assigned to your account.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="tree-manager">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl text-wedding-ivory">Wedding Tree</h1>
          <p className="text-wedding-ivory/60 mt-1">Manage guest messages and greetings</p>
        </div>
        <div className="flex items-center gap-3">
          {user?.role === "super_admin" && submissions.filter(s => s.status !== "archived").length > 0 && (
            <Button
              onClick={() => setShowClearDialog(true)}
              className="bg-red-600/20 text-red-400 hover:bg-red-600/30 border border-red-500/30 text-sm"
              data-testid="clear-all-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" /> Clear All
            </Button>
          )}
          <Filter className="w-5 h-5 text-wedding-ivory/50" />
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[180px] bg-wedding-charcoal border-white/10">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent className="bg-wedding-charcoal border-white/10">
              <SelectItem value="all">All ({statusCounts.all})</SelectItem>
              <SelectItem value="pending">Pending ({statusCounts.pending})</SelectItem>
              <SelectItem value="approved">Approved ({statusCounts.approved})</SelectItem>
              <SelectItem value="featured">Featured ({statusCounts.featured})</SelectItem>
              <SelectItem value="hidden">Hidden ({statusCounts.hidden})</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Total", value: statusCounts.all, color: "text-wedding-ivory" },
          { label: "Pending", value: statusCounts.pending, color: "text-yellow-500" },
          { label: "Approved", value: statusCounts.approved, color: "text-green-500" },
          { label: "Featured", value: statusCounts.featured, color: "text-wedding-gold" },
        ].map((stat) => (
          <div key={stat.label} className="stat-card">
            <div className={`stat-value ${stat.color}`}>{stat.value}</div>
            <div className="stat-label">{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Tree Display Settings */}
      <div className="p-6 bg-wedding-charcoal border border-white/5">
        <button onClick={() => setShowSettings(!showSettings)} className="flex items-center justify-between w-full" data-testid="toggle-tree-settings">
          <div className="flex items-center gap-3">
            <Settings className="w-5 h-5 text-wedding-gold" />
            <div>
              <h3 className="font-serif text-lg text-wedding-ivory text-left">Display Settings</h3>
              <p className="text-wedding-ivory/40 text-sm text-left">Configure tree modes for TV, tablet, and ambient display</p>
            </div>
          </div>
          <span className="text-wedding-ivory/40 text-sm">{showSettings ? "Hide" : "Show"}</span>
        </button>

        {showSettings && (
          <motion.div className="mt-6 space-y-5 pt-6 border-t border-white/5" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Default Mode */}
              <div>
                <Label className="text-wedding-ivory/60 text-sm">Default Mode</Label>
                <Select value={treeSettings.default_mode} onValueChange={(v) => setTreeSettings({...treeSettings, default_mode: v})}>
                  <SelectTrigger className="w-full bg-wedding-subtle border-white/10 mt-2" data-testid="default-mode-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-wedding-charcoal border-white/10">
                    <SelectItem value="display"><div className="flex items-center gap-2"><Monitor className="w-4 h-4" />Display (TV / Projector)</div></SelectItem>
                    <SelectItem value="interactive"><div className="flex items-center gap-2"><Hand className="w-4 h-4" />Interactive (Touchscreen)</div></SelectItem>
                    <SelectItem value="autoplay"><div className="flex items-center gap-2"><PlayCircle className="w-4 h-4" />Autoplay (Ambient)</div></SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Display Delay */}
              <div>
                <Label className="text-wedding-ivory/60 text-sm">Display Mode — Delay Between Messages (seconds)</Label>
                <Input
                  type="number" min="2" max="30" step="1"
                  value={treeSettings.display_delay}
                  onChange={(e) => setTreeSettings({...treeSettings, display_delay: parseInt(e.target.value) || 5})}
                  className="input-premium mt-2"
                  data-testid="display-delay-input"
                />
              </div>

              {/* Autoplay Delay */}
              <div>
                <Label className="text-wedding-ivory/60 text-sm">Autoplay Mode — Rotation Delay (seconds)</Label>
                <Input
                  type="number" min="3" max="30" step="1"
                  value={treeSettings.autoplay_delay}
                  onChange={(e) => setTreeSettings({...treeSettings, autoplay_delay: parseInt(e.target.value) || 8})}
                  className="input-premium mt-2"
                  data-testid="autoplay-delay-input"
                />
              </div>

              {/* Idle Timeout */}
              <div>
                <Label className="text-wedding-ivory/60 text-sm">Interactive Mode — Idle Timeout (seconds)</Label>
                <Input
                  type="number" min="10" max="120" step="5"
                  value={treeSettings.idle_timeout}
                  onChange={(e) => setTreeSettings({...treeSettings, idle_timeout: parseInt(e.target.value) || 30})}
                  className="input-premium mt-2"
                  data-testid="idle-timeout-input"
                />
              </div>
            </div>

            {/* Auto-play videos toggle */}
            <div className="flex items-center justify-between p-4 bg-wedding-subtle rounded-sm">
              <div>
                <span className="text-wedding-ivory">Auto-play Videos (Display Mode)</span>
                <p className="text-wedding-ivory/40 text-sm">Automatically play video messages during display mode cycle</p>
              </div>
              <Switch
                checked={treeSettings.autoplay_videos_enabled}
                onCheckedChange={(checked) => setTreeSettings({...treeSettings, autoplay_videos_enabled: checked})}
                data-testid="autoplay-videos-toggle"
              />
            </div>

            <div className="flex justify-end">
              <Button onClick={saveTreeSettings} disabled={savingSettings} className="btn-primary" data-testid="save-tree-settings-btn">
                {savingSettings ? <div className="w-4 h-4 border-2 border-wedding-black border-t-transparent rounded-full animate-spin" /> : <><Save className="w-4 h-4 mr-2" />Save Settings</>}
              </Button>
            </div>
          </motion.div>
        )}
      </div>

      {/* Submissions List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-12 h-12 border-2 border-wedding-gold border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : filteredSubmissions.length === 0 ? (
        <div className="text-center py-12">
          <MessageCircle className="w-16 h-16 text-wedding-gold/30 mx-auto mb-4" />
          <p className="text-wedding-ivory/60">No submissions found</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredSubmissions.map((submission, index) => (
            <motion.div
              key={submission.submission_id}
              className="card-hover p-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                {/* Thumbnail or Video Preview */}
                <div 
                  className="w-full md:w-32 h-24 bg-wedding-subtle rounded-sm overflow-hidden flex-shrink-0 cursor-pointer"
                  onClick={() => setSelectedSubmission(submission)}
                >
                  {(submission.photo_url || submission.thumbnail_url) ? (
                    <img
                      src={(() => { const u = submission.photo_url || submission.thumbnail_url; return u.startsWith("http") ? u : `${BACKEND_URL}${u}`; })()}
                      alt={submission.guest_name}
                      className="w-full h-full object-cover"
                    />
                  ) : submission.video_url ? (
                    <div className="w-full h-full flex items-center justify-center bg-wedding-charcoal">
                      <Play className="w-8 h-8 text-wedding-gold" />
                    </div>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <User className="w-8 h-8 text-wedding-ivory/30" />
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-serif text-lg text-wedding-ivory truncate">
                      {submission.guest_name || "Anonymous"}
                    </h3>
                    <span className={`badge ${
                      submission.status === "approved" ? "badge-active" :
                      submission.status === "featured" ? "bg-wedding-gold/20 text-wedding-gold border-wedding-gold/30" :
                      submission.status === "hidden" ? "badge-inactive" :
                      "badge-pending"
                    }`}>
                      {submission.status}
                    </span>
                  </div>
                  
                  {submission.message && (
                    <p className="text-wedding-ivory/60 text-sm line-clamp-2 mb-2">
                      "{submission.message}"
                    </p>
                  )}
                  
                  <p className="text-wedding-ivory/40 text-xs flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {new Date(submission.submitted_at).toLocaleDateString()}
                  </p>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  {submission.status !== "approved" && submission.status !== "featured" && (
                    <Button
                      size="sm"
                      onClick={() => updateSubmissionStatus(submission.submission_id, "approved")}
                      className="bg-green-600/20 text-green-500 hover:bg-green-600/30 border border-green-500/30"
                      data-testid={`approve-${submission.submission_id}`}
                    >
                      <Check className="w-4 h-4" />
                    </Button>
                  )}
                  {submission.status !== "featured" && (
                    <Button
                      size="sm"
                      onClick={() => updateSubmissionStatus(submission.submission_id, "featured")}
                      className="bg-wedding-gold/20 text-wedding-gold hover:bg-wedding-gold/30 border border-wedding-gold/30"
                      data-testid={`feature-${submission.submission_id}`}
                    >
                      <Star className="w-4 h-4" />
                    </Button>
                  )}
                  {submission.status !== "hidden" && (
                    <Button
                      size="sm"
                      onClick={() => updateSubmissionStatus(submission.submission_id, "hidden")}
                      className="bg-white/5 text-wedding-ivory/60 hover:bg-white/10 border border-white/10"
                      data-testid={`hide-${submission.submission_id}`}
                    >
                      <EyeOff className="w-4 h-4" />
                    </Button>
                  )}
                  <Button
                    size="sm"
                    onClick={() => toggleRaffleEligible(submission.submission_id, submission.raffle_eligible !== false)}
                    className={`border text-xs ${submission.raffle_eligible !== false ? "bg-wedding-gold/10 text-wedding-gold border-wedding-gold/30" : "bg-white/5 text-wedding-ivory/40 border-white/10"}`}
                    title={submission.raffle_eligible !== false ? "Raffle eligible — click to exclude" : "Excluded from raffle — click to include"}
                    data-testid={`raffle-toggle-${submission.submission_id}`}
                  >
                    <Ticket className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => deleteSubmission(submission.submission_id)}
                    className="bg-red-600/20 text-red-500 hover:bg-red-600/30 border border-red-500/30"
                    data-testid={`delete-${submission.submission_id}`}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Submission Detail Modal */}
      <Dialog open={!!selectedSubmission} onOpenChange={() => setSelectedSubmission(null)}>
        <DialogContent className="bg-wedding-charcoal border-white/10 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-wedding-ivory">
              {selectedSubmission?.guest_name || "Guest Message"}
            </DialogTitle>
          </DialogHeader>
          
          {selectedSubmission?.video_url && (
            <div className="video-container mt-4">
              <video
                src={selectedSubmission.video_url.startsWith("http") ? selectedSubmission.video_url : `${BACKEND_URL}${selectedSubmission.video_url}`}
                controls
                className="w-full rounded-sm"
              />
            </div>
          )}
          
          {selectedSubmission?.message && (
            <div className="mt-4 p-4 bg-wedding-subtle rounded-sm">
              <p className="text-wedding-ivory/80 font-accent italic">
                "{selectedSubmission.message}"
              </p>
            </div>
          )}
          
          <div className="mt-4 flex items-center justify-between">
            <span className="text-wedding-ivory/50 text-sm">
              Submitted {selectedSubmission && new Date(selectedSubmission.submitted_at).toLocaleString()}
            </span>
            <span className={`badge ${
              selectedSubmission?.status === "approved" ? "badge-active" :
              selectedSubmission?.status === "featured" ? "bg-wedding-gold/20 text-wedding-gold border-wedding-gold/30" :
              selectedSubmission?.status === "hidden" ? "badge-inactive" :
              "badge-pending"
            }`}>
              {selectedSubmission?.status}
            </span>
          </div>
        </DialogContent>
      </Dialog>

      {/* Clear All Confirmation Dialog */}
      <Dialog open={showClearDialog} onOpenChange={setShowClearDialog}>
        <DialogContent className="bg-wedding-charcoal border-red-500/20 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-red-400">Clear All Tree Entries</DialogTitle>
          </DialogHeader>
          <p className="text-wedding-ivory/60 text-sm mt-2">
            This will archive all {submissions.filter(s => s.status !== "archived").length} tree entries. They will disappear from the live tree and raffle pool. This action cannot be easily undone.
          </p>
          <div className="mt-4">
            <Label className="text-wedding-ivory/50 text-xs">Type <span className="text-red-400 font-bold">CLEAR ALL</span> to confirm</Label>
            <Input
              value={clearConfirmText}
              onChange={e => setClearConfirmText(e.target.value)}
              placeholder="CLEAR ALL"
              className="input-premium mt-2"
              data-testid="clear-confirm-input"
            />
          </div>
          <div className="flex gap-3 mt-4 justify-end">
            <Button onClick={() => { setShowClearDialog(false); setClearConfirmText(""); }} className="bg-white/5 text-wedding-ivory border border-white/10 hover:bg-white/10">
              Cancel
            </Button>
            <Button
              onClick={clearAllEntries}
              disabled={clearConfirmText !== "CLEAR ALL" || clearing}
              className="bg-red-600 text-white hover:bg-red-700 disabled:opacity-30"
              data-testid="confirm-clear-btn"
            >
              {clearing ? "Archiving..." : "Clear All Entries"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
